import { useLayoutEffect, useState, type RefObject } from "react";
import { DEFAULT_TETHER, opensAbove, popoverPlacement, type PopoverPlacement } from "../lib/popover";

/**
 * Measures a tethered popover against its positioned container before paint and keeps it on screen.
 * Given what it points at, it also opens above when it wouldn't fit below.
 * Until measured (and on phones, where CSS pins it to the bottom) the preferred position is used.
 */
export function usePopoverPlacement(popover: RefObject<HTMLElement | null>, preferredLeft: number, anchor?: { top: number; bottom: number }): PopoverPlacement & { above: boolean } {
  const [placement, setPlacement] = useState<PopoverPlacement & { above: boolean }>({ left: preferredLeft, tether: DEFAULT_TETHER, above: false });
  const anchorTop = anchor?.top;
  const anchorBottom = anchor?.bottom;

  useLayoutEffect(() => {
    const measure = () => {
      const element = popover.current;
      const container = element?.offsetParent as HTMLElement | null | undefined;
      if (!element || !container) return setPlacement({ left: preferredLeft, tether: DEFAULT_TETHER, above: false });
      // The preview area, not the zero-height layer that makes popovers follow the page.
      const area = element.closest<HTMLElement>(".bw-frame");
      const above = area && anchorTop !== undefined && anchorBottom !== undefined
        ? opensAbove({ top: anchorTop, bottom: anchorBottom }, element.offsetHeight, area.clientHeight)
        : false;
      setPlacement({ ...popoverPlacement(preferredLeft, element.offsetWidth, container.clientWidth), above });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [popover, preferredLeft, anchorTop, anchorBottom]);

  return placement;
}
